import { useState } from "react";
import { Collapse, Space, Tag, Typography } from "antd";
import { useT } from "../i18n/context";
import type { ToolCall } from "../types";

const { Text } = Typography;

interface ToolCallDrawerProps {
  toolCalls: ToolCall[];
}

const statusColor = (status: ToolCall["status"]) => {
  if (status === "completed") return "success";
  if (status === "denied") return "error";
  if (status === "approved") return "warning";
  return "processing";
};

export default function ToolCallDrawer({ toolCalls }: ToolCallDrawerProps) {
  const [expanded, setExpanded] = useState(false);
  const t = useT();

  const lastActive =
    [...toolCalls].reverse().find((tc) => tc.status === "pending") ??
    toolCalls[toolCalls.length - 1];
  const completed = toolCalls.filter(
    (tc) => tc.status === "completed" || tc.status === "approved",
  ).length;

  const items = toolCalls.map((tc) => ({
    key: tc.toolUseId,
    label: (
      <Space size={6}>
        <Tag color={statusColor(tc.status)} style={{ fontSize: 10 }}>
          {tc.status}
        </Tag>
        <Text style={{ fontSize: 12, fontWeight: 500 }}>{tc.toolName}</Text>
      </Space>
    ),
    children: (
      <div>
        {tc.input != null && (
          <pre style={preStyle}>{JSON.stringify(tc.input, null, 2)}</pre>
        )}
        {tc.output != null && (
          <pre style={{ ...preStyle, marginTop: 4 }}>
            {typeof tc.output === "string"
              ? tc.output
              : JSON.stringify(tc.output, null, 2)}
          </pre>
        )}
      </div>
    ),
  }));

  return (
    <div
      style={{
        width: "100%",
        marginTop: 4,
        background: "var(--lude-im-bg-secondary)",
        border: "1px solid var(--lude-im-border)",
        borderRadius: "var(--lude-im-radius-card)",
        padding: "8px 12px",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <Text style={{ fontSize: 12 }}>🔧</Text>
        <Text style={{ fontSize: 12, fontWeight: 500 }}>
          {lastActive?.toolName ?? t("tool.section_title")}
        </Text>
        <Text type="secondary" style={{ fontSize: 11, flex: 1 }}>
          {t("tool.done_count", { done: completed, total: toolCalls.length })}
        </Text>
        <Text type="secondary" style={{ fontSize: 10 }}>
          {expanded ? "▲" : "▼"}
        </Text>
      </div>
      {expanded && (
        <div style={{ maxHeight: 300, overflow: "auto", marginTop: 8 }}>
          <Collapse ghost size="small" items={items} />
        </div>
      )}
    </div>
  );
}

const preStyle = {
  background: "var(--lude-im-bg-primary)",
  padding: "8px 10px",
  borderRadius: "var(--lude-im-radius-sm)",
  fontSize: 11,
  overflow: "auto" as const,
  maxHeight: 150,
  margin: 0,
  whiteSpace: "pre-wrap" as const,
};
